import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment as env } from '../../environments/environment';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';

/**
 * Admin-only Services inspector client. Talks to the f2-admin-service2
 * /rest/admin/services routes, which read pm2 dump on the local box and
 * proxy to the peer box for cross-box reads.
 *
 * Surfaces pm2-managed processes — name/cwd/port + env (secrets masked
 * server-side) + per-file log inventory. Supports tail with paging,
 * download, and in-place truncate.
 */

export interface ServiceSummary {
  box: string;
  name: string;
  pm_id?: number;
  exec_mode?: string;
  instances?: number;
  cwd?: string;
  script?: string;
  port?: string | number | null;
  // IT-F2-215: runtime fields from `pm2 jlist` — undefined when pm2
  // daemon isn't reachable or the service isn't currently tracked
  // (dump entry exists but nothing live to match).
  status?: string;            // 'online' | 'stopped' | 'errored' | ...
  uptime_ms?: number;
  restart_count?: number;
  unstable_restarts?: number;
  cpu_percent?: number;
  memory_bytes?: number;
  logs: { combined?: string; out?: string; error?: string };
  last_modified?: string;
  total_log_size_bytes: number;
}

export interface LogFileEntry {
  file: string;
  size_bytes: number;
  mtime: string;
  kind: string;
}

export interface ServiceDetail extends ServiceSummary {
  env_masked: Record<string, string>;
  git?: { head?: string; branch?: string; remotes?: Record<string, string> };
  log_files: LogFileEntry[];
}

export interface TailPage {
  lines: string[];
  nextBefore: number | null;
  size: number;
}

/**
 * One row on the Services > Crons panel. Comes from
 * /rest/admin/services/crons — aggregation of CronRegistry (schedule
 * metadata) + latest CronRuns row (last-fire lifecycle). SPA computes
 * next_run from cron_expr + timezone using cron-parser client-side so
 * we don't need a per-row round-trip.
 */
export interface CronRow {
  service: string;
  box: string;
  cron_id: string;
  cron_expr: string;
  timezone: string | null;
  description: string | null;
  first_registered_at: string | null;
  last_registered_at: string | null;
  next_run: string | null;
  last_run: {
    started_at: string;
    finished_at: string | null;
    ok: boolean | null;
    err: string | null;
    duration_ms: number | null;
    run_id: string;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class ServicesInspectorClientService {

  constructor(
    private http: HttpClient,
    private loading: LoadingService,
    private toast: ToastService,
  ) {}

  private opts() { return { withCredentials: true }; }

  async listBoxes(): Promise<{ boxes: string[]; local: string }> {
    try {
      const res: any = await firstValueFrom(this.http.get(`${env.apiUrl}/rest/admin/services/_boxes`, this.opts()));
      return res || { boxes: [], local: '' };
    } catch (e: any) {
      this.toast.error('Services', `Boxes: ${e.message}`);
      return { boxes: [], local: '' };
    }
  }

  async list(box?: string): Promise<ServiceSummary[]> {
    const id = this.loading.add('Loading services');
    try {
      const url = box ? `${env.apiUrl}/rest/admin/services?box=${encodeURIComponent(box)}`
                      : `${env.apiUrl}/rest/admin/services`;
      const res: any = await firstValueFrom(this.http.get(url, this.opts()));
      return Array.isArray(res) ? res : [];
    } catch (e: any) {
      this.toast.error('Services', e.message);
      return [];
    } finally {
      this.loading.remove(id);
    }
  }

  async detail(box: string, name: string): Promise<ServiceDetail | null> {
    const id = this.loading.add(`Loading ${name}`);
    try {
      const res: any = await firstValueFrom(this.http.get(
        `${env.apiUrl}/rest/admin/services/${encodeURIComponent(box)}/${encodeURIComponent(name)}`,
        this.opts()
      ));
      return res || null;
    } catch (e: any) {
      this.toast.error('Service detail', e.message);
      return null;
    } finally {
      this.loading.remove(id);
    }
  }

  async tail(box: string, name: string, file: string, lines = 100, before?: number): Promise<TailPage | null> {
    try {
      const q = new URLSearchParams({ lines: String(lines) });
      if (before !== undefined) q.set('before', String(before));
      const url = `${env.apiUrl}/rest/admin/services/${encodeURIComponent(box)}/${encodeURIComponent(name)}/logs/${encodeURIComponent(file)}/tail?${q}`;
      const res: any = await firstValueFrom(this.http.get(url, this.opts()));
      return res as TailPage;
    } catch (e: any) {
      this.toast.error('Tail', e.message);
      return null;
    }
  }

  // First N lines of the file — companion to tail() for the log
  // viewer's Oldest button. No pagination cursor; the SPA jumps back
  // to live tail via Newest.
  async head(box: string, name: string, file: string, lines = 100): Promise<{ lines: string[]; size: number } | null> {
    try {
      const q = new URLSearchParams({ lines: String(lines) });
      const url = `${env.apiUrl}/rest/admin/services/${encodeURIComponent(box)}/${encodeURIComponent(name)}/logs/${encodeURIComponent(file)}/head?${q}`;
      const res: any = await firstValueFrom(this.http.get(url, this.opts()));
      return res as { lines: string[]; size: number };
    } catch (e: any) {
      this.toast.error('Head', e.message);
      return null;
    }
  }

  downloadUrl(box: string, name: string, file: string): string {
    return `${env.apiUrl}/rest/admin/services/${encodeURIComponent(box)}/${encodeURIComponent(name)}/logs/${encodeURIComponent(file)}/download`;
  }

  /**
   * Aggregate cron-run feed for the Services > Crons panel. One row per
   * registered cron across every box, plus its most-recent CronRuns row.
   * Toast-and-swallow on failure — an empty list surfaces the "no crons
   * discovered yet" empty state rather than a hard error.
   */
  async listCrons(): Promise<CronRow[]> {
    const id = this.loading.add('Loading crons');
    try {
      const res: any = await firstValueFrom(this.http.get(
        `${env.apiUrl}/rest/admin/services/crons`, this.opts()
      ));
      return Array.isArray(res) ? res as CronRow[] : [];
    } catch (e: any) {
      this.toast.error('Crons', e.message);
      return [];
    } finally {
      this.loading.remove(id);
    }
  }

  async clear(box: string, name: string, file: string): Promise<{ ok: boolean; prevSize: number } | null> {
    try {
      const res: any = await firstValueFrom(this.http.post(
        `${env.apiUrl}/rest/admin/services/${encodeURIComponent(box)}/${encodeURIComponent(name)}/logs/${encodeURIComponent(file)}/clear`,
        { confirm: true },
        this.opts()
      ));
      return res;
    } catch (e: any) {
      this.toast.error('Clear log', e.message);
      return null;
    }
  }
}
